// cSpell:ignore xyflow Silae SILAE
import { type NodeProps, type Node } from "@xyflow/react";
import { GitBranch } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { GraphNodeData } from "../utils/buildGraph";
import { effectiveConditionOperators } from "../utils/effectiveConditionOperators";
import { NodeShell } from "./NodeShell";
import { ConditionsListBlock } from "./parts/ConditionsListBlock";

/**
 * Clé i18n de la phrase introductive — sans variante "WithElse" : les
 * conditions propres à une prime ne portent pas de branche alternative dans
 * le diagramme (la prime s'applique ou est ignorée, point).
 */
function bonusConditionsDescriptionKey(
  ops: string[],
  condCount: number,
): string {
  if (condCount <= 1)
    return "step5Validation.bonusConditions.descriptionSingle";
  const effective = effectiveConditionOperators(ops, condCount);
  const unique = new Set(effective.map((o) => o.toUpperCase()));
  if (unique.size > 1)
    return "step5Validation.bonusConditions.descriptionMixed";
  const op = [...unique][0];
  return op === "OR"
    ? "step5Validation.bonusConditions.descriptionAllOr"
    : "step5Validation.bonusConditions.descriptionAllAnd";
}

export function BonusConditionsNode({ data }: NodeProps<Node<GraphNodeData>>) {
  const { t } = useTranslation();
  const conditions = data.conditions ?? [];
  const ops = data.conditionsOperators ?? [];
  const bonusLabel = (data.bonusLabel ?? "").trim();
  const i18nKey = bonusConditionsDescriptionKey(ops, conditions.length);

  return (
    <NodeShell
      variant="conditions"
      icon={GitBranch}
      title={t("step5Validation.bonusConditions.title")}
      targetStep={data.targetStep}
    >
      <ConditionsListBlock
        conditions={conditions}
        conditionsOperators={ops}
        descriptionKey={i18nKey}
        descriptionValues={{ bonus: bonusLabel }}
        descriptionComponents={{
          b: <span className="font-semibold text-zinc-900 dark:text-zinc-50" />,
        }}
      />
    </NodeShell>
  );
}
